import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import { Box, Grid2, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { ExtendedAccount } from "@features/depots/types";
import { useRoutes } from "@hooks";
import { MultibankingApiProvider } from "@api";
import { AccountPreviewChart } from "../../Chart";
import { AccountIconAndName } from "../AccountPreviewCard/AccountIconAndName";
import { AccountRowSummary } from "../AccountPreviewCard/AccountRowSummary";
import { useAccountPerformance } from "./hooks/useAccountPerformance";

const AccountPreviewCardContainer = styled.div<{
  showNotification: boolean;
  clickable: boolean;
  isLast: boolean;
}>(({ theme, clickable, isLast }) => ({
  padding: theme.spacing(2, 3),
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.border.secondary}`,
  cursor: clickable ? "pointer" : "default",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(2),
  },
  ...(isLast && {
    borderBottom: `1px solid ${theme.palette.border.secondary}`,
  }),
}));

const ChartContainer = styled(Box)({
  height: 48,
});

interface Props {
  account: ExtendedAccount;
  onClick: () => void;
  onPerformanceCalculated?: Dispatch<SetStateAction<Record<number, number>>>;
  isLast?: boolean;
  /** DOM id, so a link can anchor straight at this row. */
  rowId?: string;
}

export const AccountView = ({
  account,
  onClick,
  onPerformanceCalculated,
  isLast,
  rowId,
}: Props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isRefAccount = Boolean((account.depotIds?.length ?? 0) > 0);
  const {
    id,
    name,
    bankName,
    type,
    bankConnectionId,
    accountNumber,
    iconUrl = "",
    syncRequired,
    bankConnectionType,
    depotIds,
    typeFromBank,
  } = account;
  const { pushUpdateConnection } = useRoutes();
  const [isUpdating, setIsUpdating] = useState(false);

  const isDepot = type === "01_depot";
  const isAccount = typeFromBank === "CREDIT_CARD" || type === "02_cash";
  const needSyncAccount = Boolean(bankConnectionId) && syncRequired;

  const {
    portfolioValue,
    chartData = [],
    sinceYear,
    changeAbsCashFlowAdjusted,
    isFetching,
  } = useAccountPerformance(id, isAccount);

  useEffect(() => {
    onPerformanceCalculated?.((prevState) => ({
      ...prevState,
      [id]: portfolioValue,
    }));
  }, [onPerformanceCalculated, portfolioValue, id]);

  const handleClickUpdate = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!needSyncAccount || isUpdating) return;
    setIsUpdating(true);
    try {
      await pushUpdateConnection(
        bankConnectionId!,
        bankConnectionType as MultibankingApiProvider
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <AccountPreviewCardContainer
      id={rowId}
      data-testid="account-row"
      showNotification={needSyncAccount}
      clickable={Boolean(onClick)}
      onClick={onClick ? () => onClick() : undefined}
      isLast={isLast ?? false}
    >
      <Grid2
        container
        columnSpacing={isSm ? 1 : 3}
        alignItems="center"
        minHeight={isSm ? "48px" : "68px"}
        width="100%"
      >
        <Grid2 size="grow">
          <AccountIconAndName
            isReference={(depotIds?.length ?? 0) > 0}
            bankLogoURL={iconUrl}
            accountType={type}
            accountName={name}
            bankName={bankName}
            bankCode={accountNumber}
          />
        </Grid2>
        {!isMd && (
          <Grid2 size="grow">
            <Box height="100%" px={4}>
              <ChartContainer>
                <AccountPreviewChart
                  chartData={chartData}
                  isFetching={isFetching}
                />
              </ChartContainer>
            </Box>
          </Grid2>
        )}
        <Grid2 size={isMd ? 3.5 : 2.5}>
          <AccountRowSummary
            amount={portfolioValue}
            currency="EUR"
            sinceYear={isDepot ? sinceYear : undefined}
            changeAbs={isDepot ? (changeAbsCashFlowAdjusted ?? 0) : 0}
            showNotification={!isRefAccount && needSyncAccount}
            onClickUpdate={isRefAccount ? undefined : handleClickUpdate}
            isUpdating={isUpdating}
          />
        </Grid2>
      </Grid2>
    </AccountPreviewCardContainer>
  );
};
